import { useState } from 'react'
import { useI18n } from '../../../i18n/I18nContext'
import { workerService } from '../../../services/worker'
import type { WorkerProfileDetail } from '../../../types/access'

const initialFields = (profile: WorkerProfileDetail) => ({
  firstName: profile.firstName ?? profile.first_name ?? '',
  lastName: profile.lastName ?? profile.last_name ?? '',
  phone: profile.phone ?? '', languages: (profile.languages || []).join(', '),
  addressLine1: profile.addressLine1 ?? '', addressLine2: profile.addressLine2 ?? '',
  city: profile.city ?? '', region: profile.region ?? '', country: profile.country ?? '',
  postalCode: profile.postalCode ?? '',
  workSearchLat: String(profile.workSearchLat ?? ''), workSearchLon: String(profile.workSearchLon ?? ''),
  workRadiusKm: String(Number(profile.workSearchRadiusMeters ?? 25000) / 1000),
  emergencyName: String(profile.emergencyContact?.name ?? ''),
  emergencyPhone: String(profile.emergencyContact?.phone ?? ''),
  availability: String(profile.availability?.summary ?? ''),
})

/** Remount on the persisted revision so a refreshed profile cannot retain an old form. */
export function WorkerProfileEditor({ profile, busy, save }: {
  profile: WorkerProfileDetail; busy: boolean;
  save: (action: () => Promise<unknown>) => void;
}) {
  const { t } = useI18n()
  const [baseline] = useState(() => initialFields(profile))
  const [revision] = useState(profile.profileRevision)
  const [form, setForm] = useState(baseline)
  const [locationConsent, setLocationConsent] = useState(profile.locationConsent === true)
  const [savedConsent] = useState(profile.locationConsent === true)
  const labels: Record<keyof typeof form, string> = {
    firstName: 'First name', lastName: 'Last name', phone: 'Phone', languages: 'Languages',
    addressLine1: 'Mailing address', addressLine2: 'Address line 2', city: 'Mailing city',
    region: 'Province/state', country: 'Country', postalCode: 'Postal code',
    workSearchLat: 'Preferred work latitude', workSearchLon: 'Preferred work longitude',
    workRadiusKm: 'Matching radius (km)', emergencyName: 'Emergency contact',
    emergencyPhone: 'Emergency phone', availability: 'Availability notes',
  }
  return <form className="worker-form wm-form" onSubmit={event => {
    event.preventDefault()
    const patch: Record<string, unknown> = { expectedRevision: revision }
    for (const key of Object.keys(form) as Array<keyof typeof form>) {
      if (form[key] === baseline[key]) continue
      if (key === 'emergencyName' || key === 'emergencyPhone') {
        const contact = (patch.emergencyContact ||= {}) as Record<string, unknown>
        contact[key === 'emergencyName' ? 'name' : 'phone'] = form[key].trim() || null
      } else if (key === 'availability') patch.availability = { summary: form[key].trim() || null }
      else if (key === 'languages') patch.languages = form[key].split(',').map(value => value.trim()).filter(Boolean)
      else if (['workSearchLat', 'workSearchLon', 'workRadiusKm'].includes(key)) patch[key] = form[key].trim() ? Number(form[key]) : null
      else patch[key] = form[key].trim() || null
    }
    if (locationConsent !== savedConsent) patch.locationConsent = locationConsent
    if (Object.keys(patch).length > 1 && revision) save(() => workerService.updateProfile(patch))
  }}>
    <label className="wm-field"><span>{t('Email')}</span><input value={profile.workerEmail ?? ''} readOnly /></label>
    {(Object.keys(labels) as Array<keyof typeof form>).map(key => {
      const numeric = ['workSearchLat', 'workSearchLon', 'workRadiusKm'].includes(key)
      const bounds = key === 'workSearchLat' ? [-90, 90] : key === 'workSearchLon' ? [-180, 180] : [1, 250]
      return <label className="wm-field" key={key}><span>{t(labels[key])}</span><input
        value={form[key]} type={numeric ? 'number' : key.includes('Phone') || key === 'phone' ? 'tel' : 'text'}
        required={['firstName', 'lastName', 'phone'].includes(key) && form[key] !== baseline[key]}
        min={numeric ? bounds[0] : undefined} max={numeric ? bounds[1] : undefined} step={numeric ? 'any' : undefined}
        onChange={event => setForm({ ...form, [key]: event.target.value })} /></label>
    })}
    <label><input type="checkbox" checked={locationConsent} onChange={event => setLocationConsent(event.target.checked)} />{t('Use saved work area for matching')}</label>
    <p>{t('Preferred work coordinates are separate from GPS punch tracking and employer worksites.')}</p>
    {profile.profileCompletion?.missingFields?.length ? <p>{profile.profileCompletion.missingFields.map(field => t(field)).join(', ')}</p> : null}
    <button type="submit" className="btn btn-primary wm-button wm-button--primary" disabled={busy || !revision}>{t('Save profile')}</button>
  </form>
}
